/**
 * The agents app's state. Where you are and what is open lives here; everything else is the server's:
 * agents, folders, group chats, conversations, memory and routines are read from it and every change is
 * sent to it — this file only keeps the screen in step.
 */
import { create } from 'zustand'
import type { AgentRecord, AgentsOverview, RoutineRecord } from '@shared/agents'
import type { ActivitySegment, AppSettings, AtWork, Attachment, AuthStatus, BoardJob, ChatMessage, ChatStreamEvent, Citation, Memory, SessionUser, ToolEvent, UserEvent } from '@shared/types'
import { agentsApi, api, chatJob, chatPage, dismissJob, joinChat, memoryApi, renameMe, sendChat, stopChat, subscribeUserEvents, type ChatInfo, type Health } from './api'
import type { Agent, Folder, Group, MemoryItem, Note, Routine, Session, Space } from './data'
import { setCatalog, type ModelSpec } from '@shared/models'
import type { Palette } from './kit'

export type View = 'agents' | 'chats' | 'portfolio' | 'workers' | 'copy' | 'apps' | 'settings'
export const VIEWS: View[] = ['agents', 'chats', 'portfolio', 'workers', 'copy', 'apps', 'settings']
export type Panel = 'routines' | 'memory' | 'settings'

/** The conversation on screen: what has been loaded of it, and whether older messages wait above. */
export interface Thread {
  id: string
  messages: ChatMessage[]
  more: boolean
  loading: boolean
}
/** An answer while it is being written. */
export interface Live {
  chatId: string
  id: string
  content: string
  /** What the agent said at once, before it went to work out of sight. */
  ack: string
  reasoning: string
  citations: Citation[]
  toolEvents: ToolEvent[]
  segments: ActivitySegment[]
  startedAt: number
  notice?: string
  agent?: { id: string; name: string }
  /** A group chat: the agents at work on what was said, out of sight, and on what. Their lines land when they are said. */
  working?: AtWork[]
}
/** The job on the board of the table on screen. */
export interface Board {
  chatId: string
  job: BoardJob
}
/** A file waiting in the composer. */
export interface Pending {
  name: string
  size: number
  attachment: Attachment
}

interface State {
  /* who is here */
  ready: boolean
  user: SessionUser | null
  setupRequired: boolean
  health: Health | null
  /** When the list of models was last taken from the server (the screens that show it draw again). */
  modelsAt: number
  settings: AppSettings | null

  /* where you are */
  view: View
  /** Who the agents screen is about: an agent, or one of your group chats. */
  agentId: string
  /** The open conversation of each agent or group (null = its empty page). */
  current: Record<string, string | null>
  unread: Record<string, boolean>
  tempId: string | null
  /** Who the next temporary chat is with (none = no agent at all). */
  tempWith: string[]
  symbol: string | null
  copyId: string | null
  workerId: string | null
  /** An open talk between two agents (the conversation's id). */
  wireId: string | null
  panel: Panel | null
  routineId: string | null
  navOpen: boolean
  navHidden: boolean
  palette: boolean
  addAgent: boolean
  groupDialog: string | null
  newSpace: boolean
  openFolder: string | null
  toast: { id: number; text: string } | null

  /* what the server holds */
  spaces: Space[]
  spaceId: string
  /** false = this workspace is kept apart from the brokerage accounts. */
  brokers: boolean
  agents: Agent[]
  folders: Folder[]
  groups: Group[]
  /** The conversations loaded so far, of every agent and group chat opened. */
  sessions: Session[]
  temp: Session[]
  routines: Routine[]
  shared: Note[]

  /* the conversation on screen */
  thread: Thread | null
  live: Live | null
  /** The job the table on screen is working (or waiting on, or was stopped in). */
  board: Board | null
  streaming: boolean
  error: string | null

  init: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  setup: (username: string, password: string, name?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  saveSettings: (p: Partial<AppSettings>) => Promise<void>
  /** Change what you are called (the agents use it too). */
  rename: (name: string) => Promise<void>

  go: (view: View) => void
  openAgent: (id: string) => void
  openSession: (targetId: string, chatId: string | null) => void
  openTemp: (id: string | null) => void
  openStock: (symbol: string | null) => void
  openCopy: (id: string | null) => void
  /** Open a conversation wherever it belongs (a link, a search hit, a card). */
  openChat: (chatId: string) => Promise<void>
  set: (p: Partial<State>) => void
  say: (text: string) => void

  send: (text: string, where: { agentId: string } | { temp: true }, files?: Pending[]) => Promise<void>
  stop: () => void
  /** At a table: stop this one agent; the others go on. */
  stopAgent: (agentId: string) => void
  /** Put the stopped or waiting job on the board away. */
  dismissBoard: () => Promise<void>
  loadEarlier: () => Promise<void>
  newTemp: (withAgents?: string[]) => void
  toggleTemp: (agentId: string) => void

  addAgent_: (a: { name: string; label: string; description: string; palette: Palette }) => Promise<void>
  patchAgent: (id: string, p: Partial<Agent>) => void
  removeAgent: (id: string) => void
  addFolder: (name: string, members?: string[]) => Promise<string>
  patchFolder: (id: string, p: Partial<Folder>) => void
  removeFolder: (id: string) => void
  setFolder: (agentId: string, folderId: string | null) => void
  markUnread: (id: string, on: boolean) => void
  renameSession: (id: string, title: string) => void
  removeSession: (id: string) => void
  pinSession: (id: string, on: boolean) => void
  addGroup_: (name: string, members: string[]) => Promise<void>
  patchGroup: (id: string, p: Partial<Group>) => void
  removeGroup: (id: string) => void
  toggleMember: (groupId: string, agentId: string) => void

  loadMemory: (agentId: string) => Promise<void>
  remember: (agentId: string, text: string, kind?: MemoryItem['kind']) => void
  editMemory: (agentId: string, memId: string, text: string) => void
  forget: (agentId: string, memId: string) => void
  loadShared: () => Promise<void>
  saveNote: (n: Note) => void
  removeNote: (id: string) => void
  /** End a note on a stock now: it is kept, with when and why, and no longer read. */
  endNote: (id: string) => void

  toggleRoutine: (id: string) => void
  runRoutine: (id: string) => void
  patchRoutine: (id: string, p: Partial<Routine>) => void
  removeRoutine: (id: string) => void

  switchSpace: (id: string) => void
  addSpace: (s: { name: string; tint: string }) => Promise<void>
  patchSpace: (id: string, p: Partial<Space>) => void
  removeSpace: (id: string) => void
}

let seq = 0
let abort: AbortController | null = null
/** Which stream the screen is following: one that was left behind must not change the screen when it ends. */
let listening = 0
const SPACE_KEY = 'lb-workspace'

/* ── from the server's words to the screen's ─────────────────────────── */

const LANG: Record<string, Agent['lang']> = { english: 'English', en: 'English', türkçe: 'Türkçe', turkce: 'Türkçe', turkish: 'Türkçe', tr: 'Türkçe' }
const toAgent = (r: AgentRecord, was?: Agent): Agent => ({
  id: r.id,
  name: r.name,
  label: r.label,
  description: r.description,
  palette: r.palette,
  kind: r.main ? 'lead' : 'custom',
  skills: [],
  busy: r.busy ? 'Working' : null,
  memory: was?.memory ?? [],
  // (an agent with no language of its own answers in the one it is written to in: shown as the browser's)
  lang: LANG[(r.lang ?? '').toLowerCase()] ?? (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('tr') ? 'Türkçe' : 'English'),
  notify: r.notify,
  pinned: r.pinned,
  hidden: r.hidden,
  custom: !r.main,
})
const toSession = (c: ChatInfo, targetId: string | null): Session => ({ id: c.id, agentId: targetId, title: c.title, with: c.tempWith ?? [], at: c.updatedAt, preview: c.preview ?? '', busy: !!c.busy, pinned: c.pinned, unread: !!c.unread, wire: c.wire ?? null })
const day = (ms: number | null) => (ms ? new Date(ms).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—')
const toRoutine = (r: RoutineRecord): Routine => ({ id: r.id, name: r.name, agentId: r.agentId ?? '', instruction: r.instruction, when: r.when, next: r.on ? day(r.nextRunAt) : 'Paused', last: r.lastRunAt ? day(r.lastRunAt) : 'Never', result: 'ok', on: r.on, chatId: r.chatId })
const text = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v ?? ''))
const ago = (ms: number) => {
  const d = Math.floor((Date.now() - ms) / 86_400_000)
  return d < 1 ? 'today' : d < 2 ? 'yesterday' : d < 14 ? `${d} days ago` : new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
/** An agent's own memory: rules it was given, what it knows about its job, topics it keeps. */
const KIND: Record<string, MemoryItem['kind']> = { preference: 'rule', fact: 'about', topic: 'note' }
const toItem = (m: Memory): MemoryItem => ({ id: m.id, kind: KIND[m.kind] ?? 'about', text: text(m.value), when: ago(m.updatedAt), label: m.label })
/** The shared memory: what is always read (preferences, facts about you), topics read when relevant, and notes on stocks read with the stock. */
const toNote = (m: Memory): Note =>
  m.kind === 'note'
    ? { id: m.id, section: 'Stocks', name: m.about ?? m.label.replace(/^note:/, ''), text: text(m.value), updated: ago(m.updatedAt), kind: m.kind, span: noteSpan(m), ended: m.endedAt != null }
    : { id: m.id, section: m.kind === 'topic' ? 'Topics' : 'You', name: m.label, text: text(m.value), updated: ago(m.updatedAt), off: !!m.off, kind: m.kind }
const shortDay = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: new Date(ms).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })
/** Since when a note holds and until when — or when and why it ended. */
const noteSpan = (m: Memory) =>
  m.endedAt != null
    ? `${shortDay(m.createdAt)} – ${shortDay(m.endedAt)} · ${m.endedWhy ?? 'ended'}`
    : `Since ${shortDay(m.createdAt)} · ${m.until === 'held' ? 'while held' : m.until === 'date' && m.untilAt ? `until ${shortDay(m.untilAt)}` : 'until something new is said'}`
/** A name for a memory written by hand: its first words. */
const labelOf = (t: string) => t.trim().split(/\s+/).slice(0, 5).join(' ').replace(/[.,;:!?]+$/, '').slice(0, 48) || 'note'

export const useDemo = create<State>((set, get) => {
  const fail = (e: unknown) => get().say(e instanceof Error ? e.message : 'That did not work')
  /** Send a change, then read the screen again from the server. */
  const act = async (f: () => Promise<unknown>) => {
    try {
      await f()
    } catch (e) {
      fail(e)
    }
    await get().refresh()
  }
  const patchSessions = (f: (s: Session) => Session) => set((s) => ({ sessions: s.sessions.map(f), temp: s.temp.map(f) }))

  /** The conversations of an agent or a group chat. */
  const loadChats = async (targetId: string) => {
    const s = get()
    const group = s.groups.some((g) => g.id === targetId)
    if (!group && !s.agents.some((a) => a.id === targetId)) return
    try {
      const list = (await (group ? agentsApi.groupChats(targetId) : agentsApi.chats(targetId))).map((c) => toSession(c, targetId))
      set((x) => ({ sessions: [...x.sessions.filter((q) => q.agentId !== targetId), ...list] }))
    } catch {
      /* the list keeps what it had */
    }
  }
  const loadTemp = async () => {
    try {
      const list = (await agentsApi.temp(get().spaceId)).map((c) => toSession(c, null))
      set({ temp: list })
    } catch {
      /* keep */
    }
  }

  /** The one reducer for an answer being written, whether we sent the message or joined it half-way. */
  const onEvent = (chatId: string) => (e: ChatStreamEvent) =>
    set((s) => {
      const blank = (): Live => ({ chatId, id: '', content: '', ack: '', reasoning: '', citations: [], toolEvents: [], segments: [], startedAt: Date.now() })
      const mine = s.thread?.id === chatId
      const live = s.live?.chatId === chatId ? s.live : null
      switch (e.type) {
        case 'start':
          return { live: { ...(live && !live.id ? live : blank()), id: e.messageId, agent: e.agent } }
        case 'text':
          return live ? { live: { ...live, content: live.content + e.delta } } : {}
        case 'text_reset':
          // What was said before the work began stays as the answer's opening line.
          return live ? { live: { ...live, ack: e.reason === 'final' && !live.ack && live.content.trim() && live.content.trim().length <= 280 ? live.content.trim() : live.ack, content: '' } } : {}
        case 'reasoning': {
          if (!live) return {}
          const segs = live.segments.slice()
          const last = segs[segs.length - 1]
          if (last && last.kind === 'reasoning') segs[segs.length - 1] = { kind: 'reasoning', text: last.text + e.delta }
          else segs.push({ kind: 'reasoning', text: e.delta })
          return { live: { ...live, reasoning: live.reasoning + e.delta, segments: segs } }
        }
        case 'tool': {
          if (!live) return {}
          const evs = live.toolEvents.slice()
          const i = evs.findIndex((t) => t.id === e.event.id)
          if (i >= 0) evs[i] = e.event
          else evs.push(e.event)
          return { live: { ...live, toolEvents: evs, segments: i >= 0 ? live.segments : [...live.segments, { kind: 'tool' as const, eventId: e.event.id }] } }
        }
        case 'citations': {
          if (!live) return {}
          const urls = new Set(live.citations.map((c) => c.url))
          return { live: { ...live, citations: [...live.citations, ...e.citations.filter((c) => !urls.has(c.url))] } }
        }
        case 'title':
          return { sessions: s.sessions.map((q) => (q.id === chatId ? { ...q, title: e.title } : q)), temp: s.temp.map((q) => (q.id === chatId ? { ...q, title: e.title } : q)) }
        case 'message': {
          // In a room, an agent with nothing to add answers with a single dash: it is not shown.
          const skip = /^[—–-]$/.test(e.message.content.trim())
          const known = s.thread?.messages.some((m) => m.id === e.message.id)
          // (at a table a line lands while others are still at work: who is working stays on screen)
          return { live: live?.working?.length ? live : null, ...(mine && !skip && !known ? { thread: { ...s.thread!, messages: [...s.thread!.messages, e.message] } } : {}) }
        }
        case 'room':
          // Who is at work at the table right now (nobody: the table has fallen quiet).
          return { live: e.working.length ? { ...(live ?? blank()), working: e.working } : null }
        case 'board':
          return mine || s.board?.chatId === chatId ? { board: e.job ? { chatId, job: e.job } : null } : {}
        case 'notice':
          return live ? { live: { ...live, notice: e.text } } : {}
        case 'error':
          return { error: e.message }
        default:
          return {}
      }
    })

  /** Load a conversation's newest page, and join its answer if one is being written. */
  const loadThread = async (chatId: string) => {
    set((s) => ({ thread: { id: chatId, messages: s.thread?.id === chatId ? s.thread.messages : [], more: false, loading: true }, board: s.board?.chatId === chatId ? s.board : null }))
    try {
      const page = await chatPage(chatId)
      if (get().thread?.id !== chatId) return
      set({ thread: { id: chatId, messages: page.messages, more: page.more, loading: false } })
      patchSessions((q) => (q.id === chatId ? { ...q, unread: false } : q))
      const last = page.messages[page.messages.length - 1]
      // Where it stands: a table may be hours into a job, with its last line long said.
      const at = await chatJob(chatId).catch(() => null)
      if (get().thread?.id !== chatId) return
      set({ board: at?.job ? { chatId, job: at.job } : null })
      if (!get().streaming && (at ? at.generating : last?.role === 'user')) void join(chatId)
    } catch (e) {
      if (get().thread?.id === chatId) set({ thread: null })
      fail(e)
    }
  }
  /** Attach to an answer in progress (after a reload, or one an automation started). */
  const join = async (chatId: string) => {
    if (get().streaming) return
    abort = new AbortController()
    const mine = ++listening
    set({ streaming: true, live: { chatId, id: '', content: '', ack: '', reasoning: '', citations: [], toolEvents: [], segments: [], startedAt: Date.now() } })
    await joinChat(chatId, onEvent(chatId), abort.signal)
    if (mine !== listening) return // another stream took over meanwhile: this one's end changes nothing
    if (get().live?.chatId === chatId) set({ live: null })
    set({ streaming: false })
    if (get().thread?.id === chatId) {
      const page = await chatPage(chatId).catch(() => null)
      if (page && get().thread?.id === chatId) set((s) => ({ thread: { ...s.thread!, messages: mergeTail(s.thread!.messages, page.messages), more: s.thread!.more || page.more } }))
    }
    void get().refresh()
  }
  /** Stop listening to an answer (the server goes on writing it and keeps it). */
  const detach = () => {
    listening++
    abort?.abort()
    abort = null
    set({ streaming: false, live: null })
  }

  const applyOverview = (o: AgentsOverview) =>
    set((s) => {
      const agents = o.agents.map((r) => toAgent(r, s.agents.find((a) => a.id === r.id)))
      const groups = o.groups.map((g) => ({ id: g.id, name: g.name, members: g.members }))
      const known = (id: string) => agents.some((a) => a.id === id) || groups.some((g) => g.id === id)
      const unread: Record<string, boolean> = {}
      for (const a of o.agents) unread[a.id] = a.unread && !(s.view === 'agents' && s.agentId === a.id && !!s.current[a.id])
      for (const g of o.groups) unread[g.id] = g.unread && !(s.view === 'agents' && s.agentId === g.id && !!s.current[g.id])
      const here = o.workspaces.find((w) => w.id === o.workspaceId)
      return {
        spaces: o.workspaces.map((w) => ({ id: w.id, name: w.name, tint: w.tint })),
        spaceId: o.workspaceId,
        brokers: here?.brokers !== false,
        agents,
        folders: o.folders.map((f) => ({ id: f.id, name: f.name, members: f.members })),
        groups,
        unread,
        agentId: known(s.agentId) ? s.agentId : (agents.find((a) => !a.hidden)?.id ?? agents[0]?.id ?? ''),
      }
    })

  let unsubscribe: (() => void) | null = null
  const afterSignIn = async () => {
    // which models xAI offers now (the server asks; the newest is the default)
    void fetch('/api/models').then((r) => (r.ok ? r.json() : null)).then((j: { models?: ModelSpec[] } | null) => (j?.models?.length ? (setCatalog(j.models), useDemo.setState({ modelsAt: Date.now() })) : undefined), () => undefined)
    const [settings] = await Promise.all([api.getSettings().catch(() => null), get().refresh()])
    set({ settings })
    // Where the user is, in time: taken from this device the first time, theirs to change in Settings.
    if (settings && !settings.timeZone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (tz) void api.saveSettings({ timeZone: tz }).then((saved) => set({ settings: saved }), () => {})
    }
    void get().loadShared()
    void loadTemp()
    const a = get().agentId
    if (a) await loadChats(a)
    unsubscribe?.()
    unsubscribe = subscribeUserEvents((e: UserEvent) => {
      if (e.type === 'agents') {
        void get().refresh()
        const t = get().agentId
        if (t) void loadChats(t)
      } else if (e.type === 'tasks') void get().refresh()
      else if (e.type === 'wake') {
        const t = get().thread
        if (e.phase === 'start' && t?.id === e.chatId) void chatPage(e.chatId).then((p) => (set((s) => (s.thread?.id === e.chatId ? { thread: { ...s.thread, messages: mergeTail(s.thread.messages, p.messages) } } : {})), join(e.chatId)))
        else void get().refresh()
      }
    })
  }

  return {
    ready: false,
    user: null,
    setupRequired: false,
    health: null,
    modelsAt: 0,
    settings: null,

    view: 'agents',
    agentId: '',
    current: {},
    unread: {},
    tempId: null,
    tempWith: [],
    symbol: null,
    copyId: null,
    workerId: null,
    wireId: null,
    panel: null,
    routineId: null,
    navOpen: false,
    navHidden: false,
    palette: false,
    addAgent: false,
    groupDialog: null,
    newSpace: false,
    openFolder: null,
    toast: null,

    spaces: [],
    spaceId: localStorage.getItem(SPACE_KEY) ?? '',
    brokers: true,
    agents: [],
    folders: [],
    groups: [],
    sessions: [],
    temp: [],
    routines: [],
    shared: [],

    thread: null,
    live: null,
    board: null,
    streaming: false,
    error: null,

    init: async () => {
      window.addEventListener('lb-unauthorized', () => set({ user: null }))
      try {
        const [status, health] = await Promise.all([api.authStatus(), api.health().catch(() => null)])
        set({ user: status.user, setupRequired: status.setupRequired, health })
        if (status.user) await afterSignIn()
      } catch {
        set({ error: 'The server could not be reached' })
      }
      set({ ready: true })
    },
    login: async (username, password) => {
      await api.login({ username, password })
      const status: AuthStatus = await api.authStatus()
      set({ user: status.user })
      await afterSignIn()
    },
    setup: async (username, password, name) => {
      await api.setup({ username, password, name })
      const status = await api.authStatus()
      set({ user: status.user, setupRequired: false })
      await afterSignIn()
    },
    logout: async () => {
      detach()
      unsubscribe?.()
      unsubscribe = null
      await api.logout().catch(() => {})
      set({ user: null, agents: [], folders: [], groups: [], sessions: [], temp: [], routines: [], shared: [], thread: null, current: {}, agentId: '' })
    },
    refresh: async () => {
      try {
        const [o, routines] = await Promise.all([agentsApi.overview(get().spaceId || null), agentsApi.routines().catch(() => [] as RoutineRecord[])])
        applyOverview(o)
        localStorage.setItem(SPACE_KEY, o.workspaceId)
        const mine = new Set(o.agents.map((a) => a.id))
        set({ routines: routines.filter((r) => r.agentId && mine.has(r.agentId)).map(toRoutine) })
      } catch (e) {
        if (get().user) fail(e)
      }
    },
    rename: async (name) => {
      try {
        const { user } = await renameMe(name)
        set((s) => ({ user: s.user ? { ...s.user, name: user.name } : s.user }))
        get().say(`Your agents call you ${user.name} from now on`)
      } catch (e) {
        fail(e)
      }
    },
    saveSettings: async (p) => {
      set((s) => ({ settings: s.settings ? { ...s.settings, ...p } : s.settings }))
      try {
        set({ settings: await api.saveSettings(p) })
      } catch (e) {
        fail(e)
      }
    },

    go: (view) => {
      set((s) => ({ view, navOpen: false, wireId: null, symbol: null, copyId: null, workerId: null, routineId: view === 'agents' ? s.routineId : null, panel: view === 'agents' ? s.panel : null }))
      if (view === 'chats') void loadTemp()
    },
    openAgent: (id) => {
      set({ view: 'agents', agentId: id, navOpen: false, wireId: null, routineId: null })
      void loadChats(id)
      const open = get().current[id]
      if (open) {
        if (get().thread?.id !== open) void loadThread(open)
      } else set({ thread: null })
      if (get().agents.some((a) => a.id === id)) void get().loadMemory(id)
    },
    openSession: (targetId, chatId) => {
      if (get().live && get().live!.chatId !== chatId) detach()
      set((s) => ({ view: 'agents', agentId: targetId, current: { ...s.current, [targetId]: chatId }, navOpen: false, wireId: null, routineId: null, unread: { ...s.unread, [targetId]: false }, thread: chatId ? s.thread : null }))
      if (chatId) void loadThread(chatId)
      void loadChats(targetId)
    },
    openTemp: (id) => {
      if (get().live && get().live!.chatId !== id) detach()
      const with_ = id ? (get().temp.find((t) => t.id === id)?.with ?? []) : get().tempWith
      set({ view: 'chats', tempId: id, navOpen: false, tempWith: with_, thread: id ? get().thread : null })
      if (id) void loadThread(id)
    },
    // (a bare ticker is a US stock: the market data is asked for by its full code)
    openStock: (symbol) => set({ view: 'portfolio', symbol: symbol && !symbol.includes('.') ? `${symbol.toUpperCase()}.US` : symbol, copyId: null, navOpen: false }),
    openCopy: (id) => set({ view: 'portfolio', copyId: id, symbol: null, navOpen: false }),
    openChat: async (chatId) => {
      try {
        const { chat } = await chatPage(chatId, null, 1)
        if (chat.temp) return get().openTemp(chatId)
        const target = chat.groupId ?? chat.projectId ?? get().agents.find((a) => a.kind === 'lead')?.id ?? get().agentId
        if (chat.wire) {
          // A talk between two agents is looked at from where one is: the agent on screen stays the
          // agent on screen, so closing the talk leads back to the conversation it was opened from.
          const here = get().view === 'agents' && get().agentId ? get().agentId : target
          set({ view: 'agents', agentId: here, wireId: chatId, navOpen: false })
          return
        }
        get().openSession(target, chatId)
      } catch (e) {
        fail(e)
      }
    },
    set: (p) => set(p),
    say: (t) => {
      const id = ++seq
      set({ toast: { id, text: t } })
      window.setTimeout(() => get().toast?.id === id && set({ toast: null }), 3200)
    },

    send: async (message, where, files = []) => {
      message = message.trim()
      if (!message && !files.length) return
      const s = get()
      // An answer still being written goes on at the server. In THIS conversation the new line is handed to
      // it (you are never locked out while it works); what was on screen of it is kept until the stream is back.
      const before = s.live
      if (s.streaming) detach()
      const temp = 'temp' in where
      const target = temp ? null : where.agentId
      const group = target ? s.groups.find((g) => g.id === target) : undefined
      let chatId = temp ? s.tempId : s.current[target!]
      const fresh = !chatId
      const attachNote = files.length ? `${message ? '\n\n' : ''}${files.map((f) => `📎 ${f.name}`).join('  ')}` : ''
      const mine: ChatMessage = { id: `local-${Date.now()}`, chatId: chatId ?? '', role: 'user', content: message + attachNote, createdAt: Date.now() }
      const pending = 'pending'
      const turn = ++listening
      set((x) => ({
        error: null,
        streaming: true,
        thread: x.thread && x.thread.id === chatId ? { ...x.thread, messages: [...x.thread.messages, mine] } : { id: chatId ?? pending, messages: [mine], more: false, loading: false },
        live: before && chatId && before.chatId === chatId ? before : { chatId: chatId ?? pending, id: '', content: '', ack: '', reasoning: '', citations: [], toolEvents: [], segments: [], startedAt: Date.now() },
        ...(fresh && target ? { current: { ...x.current, [target]: pending } } : {}),
        ...(fresh && temp ? { tempId: pending } : {}),
      }))
      abort = new AbortController()
      const handle = (e: ChatStreamEvent) => {
        if (turn !== listening) return
        // A new conversation learns its id from the first event of its answer.
        if (fresh && chatId == null && (e.type === 'start' || e.type === 'message' || e.type === 'room')) {
          const id = e.type === 'message' ? e.message.chatId : e.chatId
          chatId = id
          set((x) => ({
            thread: x.thread?.id === pending ? { ...x.thread, id } : x.thread,
            live: x.live?.chatId === pending ? { ...x.live, chatId: id } : x.live,
            ...(target && x.current[target] === pending ? { current: { ...x.current, [target]: id } } : {}),
            ...(temp && x.tempId === pending ? { tempId: id } : {}),
            sessions: target ? [{ id, agentId: target as string | null, title: message.split('\n')[0].slice(0, 52) || 'New chat', with: [], at: Date.now(), preview: '', busy: true, pinned: false, unread: false, wire: null }, ...x.sessions] : x.sessions,
            temp: temp ? [{ id, agentId: null as string | null, title: message.split('\n')[0].slice(0, 52) || 'New chat', with: x.tempWith, at: Date.now(), preview: '', busy: true, pinned: false, unread: false, wire: null }, ...x.temp] : x.temp,
          }))
        }
        if (e.type === 'title' && chatId == null) return
        onEvent(chatId ?? pending)(e)
      }
      await sendChat(
        {
          chatId: chatId ?? undefined,
          message,
          attachments: files.map((f) => f.attachment),
          ...(fresh ? (temp ? { temp: true, tempWith: s.tempWith, workspaceId: s.spaceId } : group ? { groupId: group.id } : { agentId: target! }) : {}),
        },
        handle,
        abort.signal,
      )
      const id = chatId ?? pending
      if (turn !== listening) return // you wrote again, or went elsewhere: that stream's end is not this one's
      // The stream ended: whatever was still being written is now the saved answer — read the tail again.
      if (get().live?.chatId === id) set({ live: null })
      set({ streaming: false })
      if (get().error) {
        get().say(get().error!)
        if (id === pending) set((x) => ({ thread: null, ...(target ? { current: { ...x.current, [target]: null } } : { tempId: null }) }))
      }
      if (chatId && get().thread?.id === chatId) {
        const page = await chatPage(chatId).catch(() => null)
        if (page && get().thread?.id === chatId) set((x) => ({ thread: { ...x.thread!, messages: mergeTail(x.thread!.messages, page.messages), more: x.thread!.more || (page.more && x.thread!.messages.length <= page.messages.length) } }))
      }
      await get().refresh()
      if (target) void loadChats(target)
      else void loadTemp()
      const a = target && get().agents.find((q) => q.id === target)
      if (a) void get().loadMemory(a.id)
    },
    stop: () => {
      const id = get().live?.chatId ?? (get().streaming ? get().thread?.id : undefined)
      listening++
      abort?.abort()
      abort = null
      if (id && id !== 'pending') void stopChat(id)
      set({ streaming: false, live: null })
      if (id && id !== 'pending') window.setTimeout(() => get().thread?.id === id && void loadThread(id), 600)
    },
    stopAgent: (agentId) => {
      const id = get().thread?.id
      if (id && id !== 'pending') void stopChat(id, agentId)
    },
    dismissBoard: async () => {
      const b = get().board
      if (!b) return
      set({ board: null })
      await dismissJob(b.chatId).catch(fail)
    },
    loadEarlier: async () => {
      const t = get().thread
      if (!t || !t.more || t.loading || !t.messages.length) return
      set({ thread: { ...t, loading: true } })
      try {
        const page = await chatPage(t.id, t.messages[0].createdAt)
        set((s) => (s.thread?.id === t.id ? { thread: { ...s.thread, messages: [...page.messages, ...s.thread.messages], more: page.more, loading: false } } : {}))
      } catch {
        set((s) => (s.thread?.id === t.id ? { thread: { ...s.thread, loading: false } } : {}))
      }
    },
    newTemp: (withAgents = []) => {
      if (get().live) detach()
      set({ view: 'chats', tempId: null, tempWith: withAgents, navOpen: false, thread: null })
    },
    toggleTemp: (agentId) => {
      const s = get()
      const next = s.tempWith.includes(agentId) ? s.tempWith.filter((x) => x !== agentId) : [...s.tempWith, agentId]
      set({ tempWith: next, temp: s.temp.map((t) => (t.id === s.tempId ? { ...t, with: next } : t)) })
      if (s.tempId && s.tempId !== 'pending') void agentsApi.setTempWith(s.tempId, next).catch(fail)
    },

    addAgent_: async (a) => {
      try {
        const made = await agentsApi.create({ ...a, lang: get().settings?.agentLang, ws: get().spaceId })
        await get().refresh()
        set({ addAgent: false })
        get().openSession(made.id, null)
      } catch (e) {
        fail(e)
      }
    },
    patchAgent: (id, p) => {
      set((s) => ({ agents: s.agents.map((a) => (a.id === id ? { ...a, ...p } : a)) }))
      const { name, label, description, palette, notify, pinned, hidden, lang } = p
      const body = Object.fromEntries(Object.entries({ name, label, description, palette, notify, pinned, hidden, lang }).filter(([, v]) => v !== undefined))
      if (Object.keys(body).length) void agentsApi.patch(id, body).then(() => get().refresh(), fail)
    },
    removeAgent: (id) => {
      const a = get().agents.find((x) => x.id === id)
      void act(() => agentsApi.remove(id)).then(() => a && !get().agents.some((x) => x.id === id) && get().say(`${a.name} removed`))
    },
    addFolder: async (name, members = []) => {
      try {
        const { id } = await agentsApi.addFolder(name, members, get().spaceId)
        await get().refresh()
        set({ openFolder: id })
        return id
      } catch (e) {
        fail(e)
        return ''
      }
    },
    patchFolder: (id, p) => {
      set((s) => ({ folders: s.folders.map((f) => (f.id === id ? { ...f, ...p } : f)) }))
      if (p.name) void agentsApi.renameFolder(id, p.name).catch(fail)
    },
    removeFolder: (id) => {
      set((s) => ({ openFolder: s.openFolder === id ? null : s.openFolder }))
      void act(() => agentsApi.removeFolder(id))
    },
    setFolder: (agentId, folderId) => {
      set((s) => ({ folders: s.folders.map((f) => ({ ...f, members: f.id === folderId ? [...new Set([...f.members, agentId])] : f.members.filter((m) => m !== agentId) })) }))
      void act(() => agentsApi.patch(agentId, { folderId }))
    },
    markUnread: (id, on) => {
      set((s) => ({ unread: { ...s.unread, [id]: on } }))
      const chat = get().sessions.find((q) => q.agentId === id)
      if (chat) void agentsApi.setUnread(chat.id, on).catch(() => {})
    },
    renameSession: (id, title) => {
      patchSessions((q) => (q.id === id ? { ...q, title } : q))
      void api.patchChat(id, { title }).catch(fail)
    },
    pinSession: (id, on) => {
      patchSessions((q) => (q.id === id ? { ...q, pinned: on } : q))
      void api.patchChat(id, { pinned: on }).catch(fail)
    },
    removeSession: (id) => {
      set((s) => ({
        sessions: s.sessions.filter((x) => x.id !== id),
        temp: s.temp.filter((x) => x.id !== id),
        current: Object.fromEntries(Object.entries(s.current).map(([k, v]) => [k, v === id ? null : v])),
        tempId: s.tempId === id ? null : s.tempId,
        thread: s.thread?.id === id ? null : s.thread,
        wireId: s.wireId === id ? null : s.wireId,
      }))
      void api.deleteChat(id).catch(fail)
    },
    addGroup_: async (name, members) => {
      try {
        const g = await agentsApi.addGroup(name, members, get().spaceId)
        await get().refresh()
        set({ agentId: g.id, view: 'agents', groupDialog: null, wireId: null, routineId: null, thread: null })
      } catch (e) {
        fail(e)
      }
    },
    patchGroup: (id, p) => {
      set((s) => ({ groups: s.groups.map((g) => (g.id === id ? { ...g, ...p } : g)) }))
      void agentsApi.patchGroup(id, { name: p.name, members: p.members }).catch(fail)
    },
    removeGroup: (id) => {
      set((s) => ({ panel: null, thread: s.agentId === id ? null : s.thread }))
      void act(() => agentsApi.removeGroup(id))
    },
    toggleMember: (groupId, agentId) => {
      const g = get().groups.find((x) => x.id === groupId)
      if (!g) return
      get().patchGroup(groupId, { members: g.members.includes(agentId) ? g.members.filter((m) => m !== agentId) : [...g.members, agentId] })
    },

    loadMemory: async (agentId) => {
      try {
        const rows = (await api.listMemory('project', agentId)).map(toItem)
        set((s) => ({ agents: s.agents.map((a) => (a.id === agentId ? { ...a, memory: rows } : a)) }))
      } catch {
        /* keep */
      }
    },
    remember: (agentId, t) => void api.saveMemory({ scope: 'project', scopeId: agentId, kind: 'fact', label: labelOf(t), value: t }).then(() => get().loadMemory(agentId), fail),
    editMemory: (agentId, memId, t) => void memoryApi.patch(memId, { value: t }).then(() => get().loadMemory(agentId), fail),
    forget: (agentId, memId) => void api.deleteMemory(memId).then(() => get().loadMemory(agentId), fail),
    loadShared: async () => {
      try {
        set({ shared: (await api.listMemory('global')).filter((m) => m.kind !== 'portfolio' && m.kind !== 'watchlist').map(toNote) })
      } catch {
        /* keep */
      }
    },
    saveNote: (n) => {
      const known = get().shared.some((x) => x.id === n.id)
      const kind = n.section === 'Topics' ? 'topic' : (n.kind ?? 'preference')
      const done = () => get().loadShared()
      if (known) void memoryApi.patch(n.id, n.section === 'Stocks' ? { value: n.text } : { label: n.name, value: n.text, off: !!n.off, kind }).then(done, fail)
      else void api.saveMemory({ scope: 'global', kind, label: n.name, value: n.text }).then(async (m) => (n.off ? memoryApi.patch(m.id, { off: true }) : m)).then(done, fail)
    },
    removeNote: (id) => void api.deleteMemory(id).then(() => get().loadShared(), fail),
    endNote: (id) => void memoryApi.patch(id, { end: true }).then(() => get().loadShared(), fail),

    toggleRoutine: (id) => {
      const r = get().routines.find((x) => x.id === id)
      if (r) void act(() => api.updateTask(id, { enabled: !r.on }))
    },
    runRoutine: (id) => {
      set((s) => ({ routines: s.routines.map((r) => (r.id === id ? { ...r, result: 'running' } : r)) }))
      void act(() => api.runTask(id))
    },
    patchRoutine: (id, p) => void act(() => api.updateTask(id, { ...(p.name !== undefined ? { title: p.name } : {}), ...(p.instruction !== undefined ? { instructions: p.instruction } : {}) })),
    removeRoutine: (id) => {
      set((s) => ({ routineId: s.routineId === id ? null : s.routineId }))
      void act(() => api.deleteTask(id))
    },

    switchSpace: (id) => {
      if (id === get().spaceId) return
      detach()
      localStorage.setItem(SPACE_KEY, id)
      set({ spaceId: id, view: 'agents', agentId: '', sessions: [], temp: [], thread: null, current: {}, tempId: null, tempWith: [], symbol: null, copyId: null, workerId: null, wireId: null, panel: null, routineId: null, openFolder: null, navOpen: false })
      void get().refresh().then(() => {
        const a = get().agentId
        if (a) get().openAgent(a)
        void loadTemp()
      })
    },
    addSpace: async ({ name, tint }) => {
      try {
        const w = await agentsApi.addWorkspace({ name, tint })
        get().switchSpace(w.id)
        get().say(`${name} is ready`)
      } catch (e) {
        fail(e)
      }
    },
    patchSpace: (id, p) => {
      set((s) => ({ spaces: s.spaces.map((x) => (x.id === id ? { ...x, ...p } : x)) }))
      void agentsApi.patchWorkspace(id, { name: p.name, tint: p.tint }).catch(fail)
    },
    removeSpace: (id) => {
      const first = get().spaces[0]?.id
      void agentsApi.removeWorkspace(id).then(() => {
        if (get().spaceId === id && first) get().switchSpace(first)
        else void get().refresh()
      }, fail)
    },
  }
})

/** Keep what is on screen and take the saved tail over it: messages by id, optimistic ones replaced. */
function mergeTail(have: ChatMessage[], tail: ChatMessage[]): ChatMessage[] {
  if (!tail.length) return have
  const first = tail[0].createdAt
  const older = have.filter((m) => m.createdAt < first && !m.id.startsWith('local-'))
  return [...older, ...tail]
}

/* ── the address bar follows where you are, and the other way round ──── */

const toHash = (s: State) =>
  s.view === 'agents' && s.wireId
    ? `#/talk/${s.wireId}`
    : s.view === 'agents'
      ? `#/agents/${s.agentId}${s.current[s.agentId] && s.current[s.agentId] !== 'pending' ? `/${s.current[s.agentId]}` : ''}`
      : s.view === 'portfolio' && s.copyId
        ? `#/portfolio/copy/${s.copyId}`
        : s.view === 'portfolio' && s.symbol
          ? `#/portfolio/${s.symbol}`
          : s.view === 'workers' && s.workerId
            ? `#/workers/${s.workerId}`
            : s.view === 'chats' && s.tempId && s.tempId !== 'pending'
              ? `#/chats/${s.tempId}`
              : `#/${s.view}`

function fromHash(hash: string) {
  const [view, a, b] = hash.replace(/^#\/?/, '').split('/')
  const s = useDemo.getState()
  if (view === 'talk' && a) return useDemo.setState({ view: 'agents', wireId: a })
  if (!VIEWS.includes(view as View)) return
  if (view === 'agents') {
    const id = s.agents.some((x) => x.id === a) || s.groups.some((x) => x.id === a) ? a : s.agentId
    if (!id) return
    // (an address without a conversation is the agent's own page: Back from a conversation lands there, not in the conversation again)
    if (b) s.openSession(id, b)
    else if (s.current[id]) s.openSession(id, null)
    else s.openAgent(id)
  } else if (view === 'portfolio') useDemo.setState({ view: 'portfolio', copyId: a === 'copy' && b ? b : null, symbol: a && a !== 'copy' ? decodeURIComponent(a) : null })
  else if (view === 'workers') useDemo.setState({ view: 'workers', workerId: a ?? null })
  else if (view === 'chats') s.openTemp(a ?? null)
  else useDemo.setState({ view: view as View })
}

/** Start following the address bar. A link from before agents (/c/<chat>, /w/<worker>) is honoured too. */
export function startRouting() {
  const path = location.pathname
  if (path.startsWith('/join/')) history.replaceState(null, '', '/') // signed in already: the link has nothing more to do
  const old = path.match(/^\/(c|w)\/([\w-]+)/)
  if (old) {
    history.replaceState(null, '', '/')
    if (old[1] === 'c') void useDemo.getState().openChat(old[2])
    else useDemo.setState({ view: 'workers', workerId: old[2] })
  } else if (location.hash) fromHash(location.hash)
  else if (useDemo.getState().agentId) useDemo.getState().openAgent(useDemo.getState().agentId)
  // Back and Forward walk through the places visited here — they do not leave the site. Going somewhere adds
  // a step to the browser's history; the browser walking that history (or an address typed in) is followed.
  /** The address Back/Forward asked for, while the screens are still getting there. */
  let restoring = false
  const follow = () => {
    if (toHash(useDemo.getState()) === location.hash) return
    restoring = true
    fromHash(location.hash)
    window.setTimeout(() => (restoring = false), 600)
  }
  window.addEventListener('popstate', follow)
  window.addEventListener('hashchange', follow)
  useDemo.subscribe((s, was) => {
    if (!s.user) return
    const h = toHash(s)
    if (h === location.hash) return
    // The same place, now with its own address (a conversation that just got its id), the first address after
    // signing in, and the steps on the way to where Back/Forward led: these take the place of the current entry.
    const named =
      (was.view === 'agents' && s.view === 'agents' && was.agentId === s.agentId && !was.wireId && !s.wireId && was.current[was.agentId] === 'pending') ||
      (was.view === 'chats' && s.view === 'chats' && was.tempId === 'pending')
    if (restoring || named || !location.hash || !was.user) history.replaceState(null, '', h)
    else history.pushState(null, '', h)
  })
}
